import React, { useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConsentForms = ({ data, onUpdate }) => {
  const sigCanvas = useRef(null);
  const [formData, setFormData] = useState({
    hipaaConsent: data?.consent?.hipaaConsent || false,
    treatmentConsent: data?.consent?.treatmentConsent || false,
    financialConsent: data?.consent?.financialConsent || false,
    photographyConsent: data?.consent?.photographyConsent || false,
    signatureData: data?.consent?.signatureData || null,
    signatureMethod: data?.consent?.signatureMethod || 'draw',
    typedName: data?.consent?.typedName || '',
    ...data?.consent
  });

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onUpdate('consent', updated);
  };

  const handleClearSignature = () => {
    if (sigCanvas?.current) {
      sigCanvas?.current?.clear();
    }
    handleChange('signatureData', null);
  };

  const handleSaveSignature = () => {
    if (formData?.signatureMethod === 'draw' && sigCanvas?.current) {
      const signatureData = sigCanvas?.current?.toDataURL();
      handleChange('signatureData', signatureData);
    } else if (formData?.signatureMethod === 'type') {
      handleChange('signatureData', `TYPED:${formData?.typedName}`);
    }
  };

  const allConsentsChecked = formData?.hipaaConsent && 
                             formData?.treatmentConsent && 
                             formData?.financialConsent;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground mb-2 flex items-center gap-2">
          <Icon name="FileCheck" size={24} className="text-primary" />
          Consent & Authorization
        </h2>
        <p className="text-sm text-muted-foreground">
          Please review and acknowledge the following consent forms.
        </p>
      </div>

      {/* HIPAA Privacy Consent */}
      <div className="bg-background rounded-lg border border-border p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={formData?.hipaaConsent}
            onChange={(e) => handleChange('hipaaConsent', e?.target?.checked)}
          />
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">HIPAA Privacy Acknowledgment</h3>
            <p className="text-sm text-muted-foreground mb-3">
              I acknowledge that I have received and reviewed the Notice of Privacy Practices. 
              I understand that my protected health information may be used and disclosed for treatment, 
              payment, and healthcare operations as described in the notice.
            </p>
            <button className="text-sm text-primary hover:underline flex items-center gap-1">
              <Icon name="FileText" size={14} />
              View Full HIPAA Notice
            </button>
          </div>
        </div>
      </div>

      {/* Treatment Consent */}
      <div className="bg-background rounded-lg border border-border p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={formData?.treatmentConsent}
            onChange={(e) => handleChange('treatmentConsent', e?.target?.checked)}
          />
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">Consent for Treatment</h3>
            <p className="text-sm text-muted-foreground mb-3">
              I consent to the performance of dental procedures as deemed necessary or advisable by my dentist. 
              I understand that the practice of dentistry is not an exact science and acknowledge that no guarantees 
              have been made regarding treatment outcomes.
            </p>
            <button className="text-sm text-primary hover:underline flex items-center gap-1">
              <Icon name="FileText" size={14} />
              View Full Treatment Consent
            </button>
          </div>
        </div>
      </div>

      {/* Financial Consent */}
      <div className="bg-background rounded-lg border border-border p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={formData?.financialConsent}
            onChange={(e) => handleChange('financialConsent', e?.target?.checked)}
          />
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">Financial Responsibility Agreement</h3>
            <p className="text-sm text-muted-foreground mb-3">
              I understand that I am financially responsible for all charges whether or not paid by insurance. 
              I agree to pay all copayments, deductibles, and non-covered services at the time of service. 
              I authorize payment of dental benefits directly to the practice.
            </p>
            <button className="text-sm text-primary hover:underline flex items-center gap-1">
              <Icon name="FileText" size={14} />
              View Financial Policy
            </button>
          </div>
        </div>
      </div>

      {/* Photography Consent (Optional) */}
      <div className="bg-background rounded-lg border border-border p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            checked={formData?.photographyConsent}
            onChange={(e) => handleChange('photographyConsent', e?.target?.checked)}
          />
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-2">Clinical Photography Consent (Optional)</h3>
            <p className="text-sm text-muted-foreground">
              I consent to clinical photographs being taken for treatment documentation and records. 
              These images will be kept confidential and used only for clinical purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Digital Signature */}
      {allConsentsChecked && (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Pen" size={20} className="text-primary" />
            Digital Signature
          </h3>

          {/* Signature Method Selector */}
          <div className="flex gap-3 mb-4">
            <button
              onClick={() => handleChange('signatureMethod', 'draw')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                formData?.signatureMethod === 'draw' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/50'
              }`}
            >
              <Icon name="Pen" size={18} />
              <span className="font-medium">Draw Signature</span>
            </button>
            <button
              onClick={() => handleChange('signatureMethod', 'type')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                formData?.signatureMethod === 'type' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/50'
              }`}
            >
              <Icon name="Type" size={18} />
              <span className="font-medium">Type Name</span>
            </button>
          </div>

          {/* Signature Input */}
          {formData?.signatureMethod === 'draw' ? (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Draw Your Signature</label>
              <div className="border-2 border-dashed border-border rounded-lg bg-white">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    className: 'w-full h-48 rounded-lg',
                    style: { touchAction: 'none' }
                  }}
                  backgroundColor="rgb(255, 255, 255)"
                  onEnd={handleSaveSignature}
                />
              </div>
              <div className="flex justify-end mt-2">
                <Button variant="ghost" size="sm" iconName="RotateCcw" onClick={handleClearSignature}>
                  Clear
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Type Your Full Legal Name</label>
              <input
                type="text"
                value={formData?.typedName}
                onChange={(e) => {
                  handleChange('typedName', e?.target?.value);
                  handleSaveSignature();
                }}
                placeholder="Enter your full name"
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              {formData?.typedName && (
                <div className="mt-4 p-4 bg-background border border-border rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Signature Preview:</p>
                  <p className="text-3xl font-signature text-foreground italic">{formData?.typedName}</p>
                </div>
              )}
            </div>
          )}

          {/* Signature Details */}
          <div className="mt-6 bg-muted/50 rounded-lg border border-border p-4">
            <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Shield" size={18} className="text-primary" />
              Signature Verification
            </h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>Timestamp:</span>
                <span className="text-foreground font-medium">{new Date()?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Identity Verification:</span>
                <span className="text-success font-medium flex items-center gap-1">
                  <Icon name="CheckCircle" size={14} />
                  Authenticated
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Document Security:</span>
                <span className="text-success font-medium flex items-center gap-1">
                  <Icon name="Lock" size={14} />
                  Encrypted
                </span>
              </div>
            </div>
          </div>

          {/* Legal Notice */}
          <div className="mt-4 text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Legal Notice:</strong> By providing your digital signature, 
            you confirm that you have read, understood, and agree to all consent forms above. This digital 
            signature is legally binding and has the same effect as a handwritten signature.
          </div>
        </div>
      )}

      {!allConsentsChecked && (
        <div className="bg-warning/5 rounded-lg border border-warning/20 p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-warning flex-shrink-0 mt-1" />
            <p className="text-sm text-muted-foreground">
              Please review and acknowledge all required consent forms above before providing your signature.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsentForms;